import React, { createContext, useContext, useRef, useCallback, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

interface ContentNavigationContextValue {
  navigateToPage: (page: number) => void;
  navigateToTimestamp: (seconds: number) => void;
  registerPdfNavigation: (fn: ((page: number) => void) | null) => void;
  registerMediaNavigation: (fn: ((seconds: number) => void) | null) => void;
  initialPage: number | null;
  clearInitialPage: () => void;
}

const ContentNavigationContext = createContext<ContentNavigationContextValue | null>(null);

export function ContentNavigationProvider({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const pdfNavigationRef = useRef<((page: number) => void) | null>(null);
  const mediaNavigationRef = useRef<((seconds: number) => void) | null>(null);
  
  // Parse initial page from URL query parameter
  const pageParam = searchParams.get('page');
  const [initialPage, setInitialPage] = useState<number | null>(
    pageParam ? parseInt(pageParam, 10) : null
  );
  const [hasNavigated, setHasNavigated] = useState(false);

  const clearInitialPage = useCallback(() => {
    setInitialPage(null);
    setHasNavigated(true);
  }, []);

  const registerPdfNavigation = useCallback((fn: ((page: number) => void) | null) => {
    pdfNavigationRef.current = fn;
    
    // If there's an initial page and we haven't navigated yet, do it now
    if (fn && initialPage && !hasNavigated) {
      // Small delay to ensure PDF is fully rendered
      setTimeout(() => {
        fn(initialPage);
        setHasNavigated(true);
      }, 300);
    }
  }, [initialPage, hasNavigated]);

  const registerMediaNavigation = useCallback((fn: ((seconds: number) => void) | null) => {
    mediaNavigationRef.current = fn;
  }, []);

  const navigateToPage = useCallback((page: number) => {
    if (pdfNavigationRef.current) {
      pdfNavigationRef.current(page);
    }
  }, []);

  const navigateToTimestamp = useCallback((seconds: number) => {
    if (mediaNavigationRef.current) {
      mediaNavigationRef.current(seconds);
    }
  }, []);

  // Reset navigation state when URL changes
  useEffect(() => {
    const newPage = pageParam ? parseInt(pageParam, 10) : null;
    if (newPage !== initialPage) {
      setInitialPage(newPage);
      setHasNavigated(false);
    }
  }, [pageParam]);

  return (
    <ContentNavigationContext.Provider
      value={{
        navigateToPage,
        navigateToTimestamp,
        registerPdfNavigation,
        registerMediaNavigation,
        initialPage,
        clearInitialPage,
      }}
    >
      {children}
    </ContentNavigationContext.Provider>
  );
}

export function useContentNavigation() {
  const context = useContext(ContentNavigationContext);
  if (!context) {
    throw new Error("useContentNavigation must be used within ContentNavigationProvider");
  }
  return context;
}

// Safe hook that returns null if not inside provider (for optional usage)
export function useContentNavigationOptional() {
  return useContext(ContentNavigationContext);
}
